import { APP_BASE_HREF } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpRequest,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_ID, APP_INITIALIZER, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { RouteReuseStrategy } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { environment } from '@env/environment';

import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';

import { LOCATION } from '@personal-area/common';
import { ABTestEffects, abTestReducer } from '@personal-area/global/ab-test';
import { tariffReducer, TariffsEffects } from '@personal-area/global/accounts/services';
import { AuthInterceptor } from '@personal-area/global/authorization/services';
import { DEVICE_DETECTOR, DeviceDetectorProvider } from '@personal-area/global/browser-detection';
import {
  AllCountriesEffects,
  allCountriesReducer,
  CountriesEffects,
  countriesReducer,
} from '@personal-area/global/country';
import { ApiPrefixInterceptor, HeadersValidatorInterceptor } from '@personal-area/global/http/services';
import { I18nBrowserModule, I18nService } from '@personal-area/global/i18n';
import { providePartner } from '@personal-area/global/ib/models';
import { loginAnalyticsReducer } from '@personal-area/global/login-analytics';
import {
  FacebookLoginProvider,
  GoogleLoginProvider,
  SocialAuthServiceConfig,
} from '@personal-area/global/social-authorization/services';
import { metaReducerFactory } from '@personal-area/global/state';
import { UserEffects, userReducer } from '@personal-area/global/users/services';
import { verificationProviders } from '@personal-area/global/verification';
import { WebsocketFallbackService } from '@personal-area/global/websocket';
import { provideStorage } from '@personal-area/storage';
import { CookieService } from 'ngx-cookie-service';

import { appRouting } from './app-routing.module';
import {
  defaultDialogMaxHeight,
  defaultDialogMaxWidth,
  defaultDialogMinWidth,
  defaultDialogWidth,
  isServiceWorkerEnabled,
  serviceWorkerSetupDelay,
} from './constants/app.constants';
import { RequestInterceptor } from './core/interceptors/request.interceptor';
import { RouteReusableStrategy } from './core/routing/route-reusable-strategy';
import { GlobalWebsocketFallbackService } from './core/services/user/global.websocket-fallback.service';

export function getAuthServiceConfigs(
  client: HttpClient,
  location: Location,
): SocialAuthServiceConfig {
  const host = location.hostname;

  const facebook =
    environment.social.facebook.find((i) => i.domains.includes(host)) ||
    environment.social.facebook.find((i) => i.domains.includes('default'));
  const googleAppID =
    environment.social.google.find((i) => i.domains.includes(host))?.appId ||
    environment.social.google.find((i) => i.domains.includes('default')).appId;

  return {
    providers: [
      {
        id: GoogleLoginProvider.PROVIDER_ID,
        provider: new GoogleLoginProvider(googleAppID, client),
      },
      {
        id: FacebookLoginProvider.PROVIDER_ID,
        provider: new FacebookLoginProvider(facebook?.appId || null, {
          scope: 'email, public_profile',
          fields: 'token_for_business',
          locale: 'en_US',
          version: 'v4.0',
        }),
      },
    ],
  };
}

export const appProviders = [
  provideZoneChangeDetection(),
  provideHttpClient(withInterceptorsFromDi(), withFetch()),
  provideAnimationsAsync(),
  provideClientHydration(
    withEventReplay(),
    withHttpTransferCacheOptions({
      includePostRequests: false,
      filter: (req: HttpRequest<unknown>) => !req.url.includes('/assets/i18n/'),
    }),
  ),
  provideStore(
    {
      countries: countriesReducer,
      allCountries: allCountriesReducer,
      tariffs: tariffReducer,
      user: userReducer,
      abTest: abTestReducer,
      'login-analytics': loginAnalyticsReducer,
    },
    {
      metaReducers: metaReducerFactory(),
    },
  ),
  provideEffects([
    CountriesEffects,
    AllCountriesEffects,
    TariffsEffects,
    UserEffects,
    ABTestEffects,
  ]),
  {
    multi: true,
    provide: HTTP_INTERCEPTORS,
    useClass: HeadersValidatorInterceptor,
  },
  {
    multi: true,
    provide: HTTP_INTERCEPTORS,
    useClass: ApiPrefixInterceptor,
  },
  {
    multi: true,
    provide: HTTP_INTERCEPTORS,
    useClass: RequestInterceptor,
  },
  {
    multi: true,
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
  },
  {
    provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
    useValue: {
      appearance: 'outline',
      subscriptSizing: 'dynamic',
    },
  },
  {
    provide: MAT_DIALOG_DEFAULT_OPTIONS,
    useValue: {
      closeOnNavigation: true,
      maxWidth: defaultDialogMaxWidth,
      minWidth: defaultDialogMinWidth,
      width: defaultDialogWidth,
      maxHeight: defaultDialogMaxHeight,
      panelClass: ['common-popup-container'],
    },
  },
  {
    provide: DEVICE_DETECTOR,
    useFactory: DeviceDetectorProvider,
  },
  { provide: APP_ID, useValue: environment.appId },
  {
    provide: 'SocialAuthServiceConfig',
    useFactory: getAuthServiceConfigs,
    deps: [HttpClient, LOCATION],
  },
  { provide: APP_BASE_HREF, useValue: '/cabinet' },
  {
    provide: APP_INITIALIZER,
    deps: [I18nService, CookieService],
    useFactory: I18nService.initializeI18nFactory,
    multi: true,
  },
  {
    provide: WebsocketFallbackService,
    useClass: GlobalWebsocketFallbackService,
  },
  provideServiceWorker('/cabinet/ngsw-worker-polyfill.js', {
    enabled: isServiceWorkerEnabled,
    registrationStrategy: `registerWhenStable:${serviceWorkerSetupDelay}`,
  }),
  {
    provide: RouteReuseStrategy,
    useClass: RouteReusableStrategy,
  },
  provideStorage({
    isTest: environment.isStage,
    secretKey: environment.secretKey,
    version: 'v1',
  }),
  importProvidersFrom(
    TranslateModule,
    I18nBrowserModule,
  ),
  providePartner({ isStandalone: false }),
  verificationProviders,
  appRouting,
];
